import { Trans, useTranslation } from 'react-i18next'
import { Box, TextField, Typography } from '@mui/material'
import { CustomSwitch } from '../atoms/CustomSwitch'
import { useEffect, useState } from 'react'

export interface FuseMeteringSettings {
  enabled: boolean
  protectFuses: boolean
  fuseSize: number
}

// Main-fuse size bounds (A), mirrored by the server (Sunergos.Protocol.Ports.Optimizer).
// Finnish household main fuses are 3x16..3x200 A; 630 A is the largest standard fuse of a
// low-voltage connection.
export const FUSE_SIZE_MIN = 10
export const FUSE_SIZE_MAX = 630

export function isValidFuseSize(fuseSize: number): boolean {
  return Number.isFinite(fuseSize) && fuseSize >= FUSE_SIZE_MIN && fuseSize <= FUSE_SIZE_MAX
}

/**
 * Whether the settings can be saved: the fuse size only matters while fuse protection is on.
 */
export function isValidFuseSettings(settings: FuseMeteringSettings): boolean {
  return !settings.protectFuses || isValidFuseSize(settings.fuseSize)
}

export interface FuseProtectionFormProps {
  settings: FuseMeteringSettings
  onChange: (settings: FuseMeteringSettings) => void
}

export function FuseProtectionForm(props: FuseProtectionFormProps) {
  const { settings, onChange } = props
  const [fuseSize, setFuseSize] = useState(`${settings.fuseSize}` || '')
  const { t } = useTranslation()

  useEffect(() => {
    setFuseSize(`${settings.fuseSize}` || '')
  }, [settings])

  return <Box>
    {
      !settings.enabled &&
      <Typography variant="h6" color="primary.main">
        <Trans>Please add an Enion device to enable fuse protection settings</Trans>
      </Typography>
    }
    <Box>
      <CustomSwitch
        disabled={!settings.enabled}
        data-cy="switch-Protect Fuses"
        onClick={() => onChange({ ...settings, protectFuses: !settings.protectFuses })}
        checked={settings.protectFuses}
        sx={{ ml: '1rem' }}
      />
      <Typography component="span" sx={{ ml: 2 }}>
        {t('Protect fuses by turning appliances off')}
      </Typography>
    </Box>

    {settings.protectFuses && <Box sx={{ mt: 2, ml: 2 }}>
      <Trans>Fuse size</Trans>:
      <TextField
        data-cy="text-Fuse size"
        disabled={!settings.enabled}
        margin="normal"
        sx={{ width: '7rem', mt: -1, ml: 2, verticalAlign: 'top' }}
        size="small"
        value={fuseSize}
        type="number"
        inputProps={{ min: FUSE_SIZE_MIN, max: FUSE_SIZE_MAX, step: 1 }}
        // Typing must replace the old value: a caret left next to "25" turned "35" into 2535.
        onFocus={(e) => e.target.select()}
        onChange={(e) => setFuseSize(e.target.value)}
        onBlur={() => onChange({ ...settings, fuseSize: fuseSize ? parseInt(fuseSize) : 0 })}
        error={!isValidFuseSize(parseInt(fuseSize))}
        helperText={!isValidFuseSize(parseInt(fuseSize)) &&
          t('Fuse size must be between {{min}} and {{max}} A', { min: FUSE_SIZE_MIN, max: FUSE_SIZE_MAX })}
      />&nbsp;A
    </Box>}

  </Box>
}
